import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";

const CauseAnalysisCiiRating = ({ causeAnalysisCiiRating, refetchMonitoringData, vesselMstId }) => {
	const [data, setData] = useState([]);
	const [validationErrors, setValidationErrors] = useState({});
	const [activeAction, setActiveAction] = useState(-1);
	const RowActions = { Edit: 0, Add: 1, Delete: 2 };
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [deletingRowIndex, setDeletingRowIndex] = useState();
	const handleDeleteBtnClick = (rowIndex) => {
		setConfirmDelete(true);
		setDeletingRowIndex(rowIndex);
	};

	useEffect(() => {
		const cleanedData = (causeAnalysisCiiRating || []).map((item) => ({
			cause: item.cause ?? "",
			effect: item.effect ?? "",
			action: item.action ?? "",
		}));
		setData(cleanedData);
	}, [causeAnalysisCiiRating]);

	const resetActiveRow = () => {
		setActiveAction(-1);
		setValidationErrors({});
	};

	const handleActions = (table, row, action) => {
		if (activeAction === -1) {
			if (action === RowActions.Edit) table.setEditingRow(row);
			else if (action === RowActions.Add) table.setCreatingRow(true);
			setActiveAction(action);
		}
	};

	const handleDelete = async (rowIndex) => {
		const updatedData = data.filter((_, index) => index !== rowIndex);
		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.SeempIII62(vesselMstId),
				data: updatedData,
			});
			if (res?.isSuccess) {
				emitToasterMsg("success", "Table 6.2", "Analysis of causes for inferior CII rating  deleted successfully");
				setData(updatedData);
				setActiveAction(-1);
				setConfirmDelete(false);
				await refetchMonitoringData?.();
			} else {
				emitToasterMsg("error", "Table 6.2", res?.errorMessage);
				setConfirmDelete(false);
			}
		} catch (error) {
			emitToasterMsg("error", "Table 6.2", error.errorMessage);
		}
	};

	const handleSave = async ({ values, table, row }) => {
		const isNewRow = row.id?.toString().includes("mrt-row-create");
		const newEntry = {
			cause: values.cause.trim(),
			effect: values.effect.trim(),
			action: values.action.trim(),
		};

		const finalPayload = isNewRow ? [...data, newEntry] : data.map((item, index) => (index === row.index ? { ...item, ...newEntry } : item));

		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.SeempIII62(vesselMstId),
				data: finalPayload,
			});
			if (res?.isSuccess) {
				emitToasterMsg(
					"success",
					"Table 6.2",
					isNewRow
						? "Analysis of causes for inferior CII rating added successfully"
						: "Analysis of causes for inferior CII rating updated successfully"
				);
				isNewRow ? table.setCreatingRow(null) : table.setEditingRow(null);
				setData(finalPayload);
				setActiveAction(-1);
				setValidationErrors({});
				await refetchMonitoringData?.();
			} else {
				emitToasterMsg("error", "Table 6.2", res?.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Table 6.2", error.errorMessage);
		}
	};

	const safeValue = (value) => (typeof value === "string" ? value : "");
	const safeLength = (value) => safeValue(value).length;

	const columns = useMemo(
		() => [
			{
				accessorKey: "cause",
				header: "Cause",
				muiEditTextFieldProps: ({ cell }) => {
					const value = safeValue(cell.getValue());
					return {
						required: true,
						defaultValue: value,
						inputProps: { maxLength: 1000 },
						error: !!validationErrors?.cause,
						helperText: validationErrors?.cause,
						FormHelperTextProps: { sx: { color: "#002A4E", fontSize: "0.57rem" } },
						onFocus: () => setValidationErrors((prev) => ({ ...prev, cause: undefined })),
					};
				},
			},

			{
				accessorKey: "effect",
				header: "Analysis of effect",
				muiEditTextFieldProps: ({ cell }) => {
					const value = safeValue(cell.getValue());
					return {
						required: true,
						defaultValue: value,
						inputProps: { maxLength: 1000 },
						error: !!validationErrors?.effect,
						helperText: validationErrors?.effect,
						FormHelperTextProps: { sx: { color: "#002A4E", fontSize: "0.57rem" } },
						onFocus: () => setValidationErrors((prev) => ({ ...prev, effect: undefined })),
					};
				},
			},
			{
				accessorKey: "action",
				header: "Actions",
				muiEditTextFieldProps: ({ cell }) => {
					const value = safeValue(cell.getValue());
					return {
						required: true,
						defaultValue: value,
						inputProps: { maxLength: 1000 },
						error: !!validationErrors?.action,
						helperText: validationErrors?.action,
						FormHelperTextProps: { sx: { color: "#002A4E", fontSize: "0.57rem" } },
						onFocus: () => setValidationErrors((prev) => ({ ...prev, action: undefined })),
					};
				},
			},
		],
		[validationErrors]
	);

	const table = useMaterialReactTable({
		columns,
		data: data || [],
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableTopToolbar: true,
		enableBottomToolbar: false,
		enableColumnActions: false,
		enableGlobalFilter: false,
		enableColumnFilters: false,
		createDisplayMode: "row",
		editDisplayMode: "row",
		enableEditing: true,
		enablePagination: false,
		enableDensityToggle: false,
		enableFullScreenToggle: false,
		enableColumnVisibility: false,
		enableHiding: false,
		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": {
					borderRight: "none",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "520px",
			},
		},

		muiTableBodyCellProps: ({ row, cell }) => {
			return {
				sx: {
					borderRight: "1px solid lightgray",
					"&:last-child": {
						borderRight: "none",
					},
					color: "",
				},
			};
		},
		muiTableBodyRowProps: ({ row }) => ({
			sx: {
				"&:not(:last-of-type)": {
					borderBottom: "1px solid lightgray",
				},
				verticalAlign: "top",
			},
		}),
		muiTableHeadRowProps: {
			sx: {
				borderBottom: "2px solid lightgray",
			},
		},
		onCreatingRowSave: handleSave,
		onEditingRowSave: handleSave,
		onEditingRowCancel: resetActiveRow,
		onCreatingRowCancel: resetActiveRow,
		renderTopToolbarCustomActions: ({ table }) => (
			<Button startIcon={<Icon icon={ICONS.ADD} />} onClick={() => handleActions(table, null, RowActions.Add)} disabled={!!table.getState().creatingRow}>
				Add
			</Button>
		),
		renderRowActions: ({ row, table }) => (
			<Box sx={{ display: "flex", gap: "1rem" }}>
				<Tooltip title="Edit">
					<IconButton onClick={() => handleActions(table, row, RowActions.Edit)}>
						<EditIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Delete">
					<IconButton color="error" onClick={() => handleDeleteBtnClick(row?.index, true)}>
						<DeleteIcon />
					</IconButton>
				</Tooltip>
			</Box>
		),
	});

	return (
		<>
			<div className="carbon-capture-table mt-1">
				<MaterialReactTable table={table} />
			</div>
			<div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={() => {
									handleDelete(deletingRowIndex);
								}}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>

							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete this item?</div>
				</Modal>
			</div>
		</>
	);
};

export default CauseAnalysisCiiRating;
